import { DS_HEIGHT } from '@calm/evexia-design-tokens';
import {
    Box,
    Grid,
    GridProps,
    Stack,
    SxProps,
    Theme,
    Typography,
    TypographyProps,
} from '@rhg/libs/material';
import { sxAppend } from '@rhg/libs/material/sx';
import { ElementType, ReactNode } from 'react';
import { TextWithLinks } from '../text-with-links';

const styleSubtitle = {
    whiteSpace: 'pre-line',
};

interface HeadingProps {
    title?: string;
    titleVariant?: TypographyProps['variant'];
    titleColor?: TypographyProps['color'];
    titleComponent?: ElementType;
    titleAlign?: TypographyProps['textAlign'];
    titleStyle?: SxProps<Theme>;
    titleColumns?: Pick<GridProps, 'xl' | 'lg' | 'md' | 'sm' | 'xs'>;
    titleId?: string;
    titleAriaLabel?: string;
    subtitle?: string;
    subtitleVariant?: TypographyProps['variant'];
    subtitleColor?: TypographyProps['color'];
    subtitleComponent?: ElementType;
    subtitleAlign?: TypographyProps['textAlign'];
    subtitleStyle?: SxProps<Theme>;
    subtitleWillRenderLinks?: boolean;
    subTitleColumns?: Pick<GridProps, 'xl' | 'lg' | 'md' | 'sm' | 'xs'>;
    spacing?: number;
    /**
     * If `true`, the subtitle will render above the title.
     */
    reversed?: boolean;
    /** Renders to the left of the heading */
    leftButton?: ReactNode;
    /** Renders to the right of the heading */
    rightButton?: ReactNode;
    sx?: SxProps<Theme>;
    ['data-testid']?: string;
}

const stylePlaceholder = {
    height: DS_HEIGHT.BUTTON_LARGE,
    minWidth: DS_HEIGHT.BUTTON_LARGE,
};

export function Placeholder({ sx }: { sx?: SxProps<Theme> }) {
    return <Box sx={sxAppend(stylePlaceholder, sx)} />;
}

const styleHiddenXl = {
    display: { xs: 'block', xl: 'none' },
};
export function PlaceholderHiddenXl({ sx }: { sx?: SxProps<Theme> }) {
    return <Box sx={sxAppend(stylePlaceholder, styleHiddenXl, sx)} />;
}

export function Heading({
    title,
    titleVariant = 'h4',
    titleColor = 'text.primary',
    titleComponent = 'h1',
    titleAlign = 'left',
    titleStyle,
    titleColumns,
    titleId,
    titleAriaLabel,
    subtitle,
    subtitleVariant = 'subtitle1',
    subtitleColor = 'text.sub',
    subtitleComponent = 'p',
    subtitleAlign = 'left',
    subtitleStyle,
    subtitleWillRenderLinks = false,
    subTitleColumns,
    spacing = 2,
    reversed = false,
    rightButton,
    leftButton,
    sx,
}: HeadingProps) {
    return (
        <Grid
            container
            sx={sx}
            direction="row"
            flexWrap="nowrap"
            alignItems="center"
        >
            <Grid
                container
                direction={reversed ? 'column-reverse' : 'column'}
                alignItems="center"
                justifyContent="center"
                gap={spacing}
                flexWrap="nowrap"
            >
                {title && (
                    <Stack direction="row" width="100%" gap={5}>
                        {leftButton ? (
                            <Grid item flexShrink={0}>
                                {leftButton}
                            </Grid>
                        ) : rightButton && titleAlign === 'center' ? (
                            <PlaceholderHiddenXl />
                        ) : null}
                        <Grid
                            container
                            justifyContent={
                                titleAlign !== 'center'
                                    ? 'flex-start'
                                    : 'center'
                            }
                            flexGrow={1}
                            alignItems="center"
                        >
                            <Grid item {...titleColumns} flexGrow={1}>
                                <Typography
                                    component={titleComponent}
                                    variant={titleVariant}
                                    color={titleColor}
                                    textAlign={titleAlign}
                                    sx={titleStyle}
                                    id={titleId}
                                    aria-label={titleAriaLabel}
                                >
                                    {title}
                                </Typography>
                            </Grid>
                        </Grid>
                        {rightButton ? (
                            <Grid item flexShrink={0}>
                                {rightButton}
                            </Grid>
                        ) : leftButton && titleAlign === 'center' ? (
                            <PlaceholderHiddenXl />
                        ) : null}
                    </Stack>
                )}
                {subtitle && (
                    <Grid
                        container
                        justifyContent={
                            subtitleAlign !== 'center' ? 'flex-start' : 'center'
                        }
                    >
                        <Grid item {...subTitleColumns}>
                            <Typography
                                component={subtitleComponent}
                                variant={subtitleVariant}
                                color={subtitleColor}
                                textAlign={subtitleAlign}
                                sx={sxAppend(styleSubtitle, subtitleStyle)}
                            >
                                {subtitleWillRenderLinks ? (
                                    <TextWithLinks text={subtitle} />
                                ) : (
                                    subtitle
                                )}
                            </Typography>
                        </Grid>
                    </Grid>
                )}
            </Grid>
        </Grid>
    );
}
